import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input, signal, ViewEncapsulation, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import { CheckboxItem } from '@celum/common-components/checkbox-item';
import { ColorConstants } from '@celum/common-components/constants';
import { CelumIcon, IconConfiguration } from '@celum/common-components/icon';
import { CelumFormFieldDirective } from '@celum/common-components/inputs';
import { hasValue } from '@celum/core';
import { SpecFieldInfo } from '@celum/formats/domain';
import { collectSpecFieldInfo, SpecificationStore } from '@celum/formats/shared';
import { FnWrapperPipe } from '@celum/ng2base/pipes';
import { CelumChipComponent, CelumChipSetComponent } from '@celum/shared/ui';

@Component({
  selector: 'spec-params',
  templateUrl: './spec-params.component.html',
  imports: [
    CommonModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatInput,
    MatSelectModule,
    MatTabsModule,
    MatTooltipModule,
    ReactiveFormsModule,
    TranslateModule,

    CelumChipComponent,
    CelumChipSetComponent,
    CelumFormFieldDirective,
    CelumIcon,
    CheckboxItem,
    FnWrapperPipe
  ],
  styleUrls: ['../format-view-shared.scss', 'spec-params.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SpecParamsComponent {
  public specFormGroup = input<FormGroup<Record<string, FormControl>>>();
  public showHiddenFields = input<boolean>(false);

  protected readonly infoIcon = IconConfiguration.small('info-icon').withColor(ColorConstants.BLUE_GRAY_900).withIconSize(12);
  protected readonly arrowIcon = IconConfiguration.xSmall('arrow-down-xs').withColor(ColorConstants.BLUE_GRAY_900).withIconSize(12);

  protected readonly specParameters = inject(SpecificationStore).specParameters;
  protected readonly groups = inject(SpecificationStore).groups;

  protected readonly fieldInfoMap = computed(() => {
    const params = this.specParameters();
    if (!params) {
      return new Map<string, SpecFieldInfo>();
    }

    return new Map(params.filter(param => hasValue(param)).map(param => [param.key, collectSpecFieldInfo(param)]));
  });
  protected readonly selectedValuesSignals = new Map<string, WritableSignal<string[]>>();

  private readonly dependencyParametersSignals = new Map<string, WritableSignal<unknown>>();
  private readonly searchControls = new Map<string, FormControl<string>>();
  private readonly filteredOptionsSignals = new Map<string, WritableSignal<string[]>>();

  private destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const fieldInfoMap = this.fieldInfoMap();
      this.specParameters()
        ?.filter(param => hasValue(param))
        .forEach(param => {
          const fieldInfo = fieldInfoMap.get(param.key);
          if (!fieldInfo) {
            return;
          }

          // Create signals for parameters that other parameters depend on
          if (fieldInfo.dependsOn) {
            const depName = fieldInfo.dependsOn.dependsOnParameterName.default;
            if (!this.dependencyParametersSignals.has(depName)) {
              const formControl = this.specFormGroup()?.get(depName);
              if (formControl) {
                const depSignal = signal<unknown>(formControl.value);
                this.dependencyParametersSignals.set(depName, depSignal);
                formControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => depSignal.set(value));
              }
            }
          }

          if (fieldInfo.dropdown?.selectionType !== 'multiple') {
            return;
          }

          const key = param.key;
          const options = fieldInfo.dropdown.values;

          if (!this.searchControls.has(key)) {
            const searchControl = new FormControl('');
            this.searchControls.set(key, searchControl);
            const filtered = signal(options);
            this.filteredOptionsSignals.set(key, filtered);

            searchControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(term => {
              const lower = (term ?? '').toLowerCase();
              filtered.set(lower ? options.filter(opt => opt.toLowerCase().includes(lower)) : options);
            });
          }

          if (!this.selectedValuesSignals.has(key)) {
            const formControl = this.specFormGroup()?.get(key);
            if (!formControl) {
              return;
            }
            const selected = signal<string[]>(formControl.value ?? []);
            this.selectedValuesSignals.set(key, selected);

            formControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
              selected.set(value ?? []);
            });
          }
        });
    });
  }

  protected getSearchControl = (key: string): FormControl<string> => {
    return this.searchControls.get(key) ?? new FormControl('');
  };

  protected getFilteredOptions = (key: string): string[] => {
    return this.filteredOptionsSignals.get(key)?.() ?? [];
  };

  protected isChecked = (key: string, option: string): boolean => {
    return (this.selectedValuesSignals.get(key)?.() ?? []).includes(option);
  };

  protected shouldShowParam = (showHiddenFields: boolean, fieldInfo: SpecFieldInfo, allGroups: string[] | null, selectedGroup: string): boolean => {
    if (fieldInfo.dependsOn) {
      const dependsOnParamName = fieldInfo.dependsOn.dependsOnParameterName.default;
      const allowedValues = fieldInfo.dependsOn.displayedWhenParameterValueIn.default;
      const currentValue = this.dependencyParametersSignals.get(dependsOnParamName)?.();

      if (
        currentValue === null ||
        (typeof currentValue === 'string' && !allowedValues.includes(currentValue)) ||
        (Array.isArray(currentValue) && allowedValues.some(singleAllowedValue => !currentValue.includes(singleAllowedValue)))
      ) {
        return false;
      }
    }

    return (
      (showHiddenFields || !fieldInfo.excludeFromUI) &&
      (!allGroups?.length || fieldInfo.group?.includes(selectedGroup) || (allGroups.length && !fieldInfo.group && allGroups[0] === selectedGroup))
    );
  };

  protected toggleOption(key: string, option: string, checked: boolean): void {
    const control = this.specFormGroup().get(key);
    const current: string[] = control?.value ?? [];
    control?.setValue(checked ? [...current, option] : current.filter(curr => curr !== option));
  }

  protected removeChip(key: string, value: string): void {
    const control = this.specFormGroup().get(key);
    control?.setValue((control.value as string[]).filter(ctrlValue => ctrlValue !== value));
  }

  protected trackByFn(index: number, field: string): string {
    return field + index;
  }
}
